import { Component } from '@theme/component';
import { CartUpdateEvent } from '@theme/events';

/**
 * Converts an existing one-time cart line into a subscription.
 *
 * Shopify has no "change the selling plan of this line" endpoint, so the conversion is a
 * change: the line's quantity is zeroed and the same variant is re-added carrying the
 * chosen selling_plan. Doing it in that order keeps the cart from briefly holding two
 * copies of the product if the second call fails.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} pills
 * @property {HTMLButtonElement} addButton
 * @property {HTMLElement} [error]
 * @extends {Component<Refs>}
 */
class ShavingartCartMembership extends Component {
  requiredRefs = ['pills', 'addButton', 'options'];

  #busy = false;

  /** The card starts collapsed; the first click reveals the cadence options. */
  #expanded = false;

  /** @param {MouseEvent} event */
  selectPlan(event) {
    const pill = /** @type {HTMLElement | null} */ (event.target)?.closest('[data-plan-id]');
    if (!pill) return;
    this.#select(pill);
  }

  /**
   * The pills are a radiogroup, so arrow keys move the selection and Space/Enter commits
   * the focused one — matching how the PDP picker behaves.
   * @param {KeyboardEvent} event
   */
  onKeydown(event) {
    const pills = /** @type {HTMLElement[]} */ ([...this.refs.pills.querySelectorAll('[data-plan-id]')]);
    const current = /** @type {HTMLElement | null} */ (event.target)?.closest('[data-plan-id]');
    if (!current) return;
    const index = pills.indexOf(/** @type {HTMLElement} */ (current));
    if (index === -1) return;

    let next = null;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = pills[(index + 1) % pills.length];
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = pills[(index - 1 + pills.length) % pills.length];
        break;
      case ' ':
      case 'Enter':
        next = current;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#select(next);
    next.focus();
  }

  /** @param {Element} pill */
  #select(pill) {
    for (const el of this.refs.pills.querySelectorAll('[data-plan-id]')) {
      const selected = el === pill;
      el.classList.toggle('is-selected', selected);
      el.setAttribute('aria-checked', String(selected));
      el.setAttribute('tabindex', selected ? '0' : '-1');
    }
  }

  get #planId() {
    const selected = this.refs.pills.querySelector('[data-plan-id].is-selected')
      ?? this.refs.pills.querySelector('[data-plan-id]');
    return selected instanceof HTMLElement ? selected.dataset.planId : undefined;
  }

  async convert() {
    if (this.#busy) return;

    // First click only opens the picker — the shopper confirms the cadence on the second.
    if (!this.#expanded) {
      this.#expand();
      return;
    }

    const planId = this.#planId;
    const { variantId, lineKey, quantity } = this.dataset;
    if (!planId || !variantId || !lineKey) return;

    this.#busy = true;
    this.refs.addButton.disabled = true;
    this.#setError('');

    try {
      // Remove the one-time line first so a failure can't leave a duplicate behind.
      const removed = await fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 }),
      });
      if (!removed.ok) throw new Error('Could not update the cart');

      const sections = this.#cartSectionIds();
      const added = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: Number(variantId),
          quantity: Number(quantity) || 1,
          selling_plan: Number(planId),
          ...(sections ? { sections } : {}),
        }),
      });
      const data = await added.json();
      if (data.status) throw new Error(data.description || data.message);

      this.dispatchEvent(
        new CartUpdateEvent({}, this.dataset.lineKey ?? '', {
          source: 'shavingart-cart-membership',
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error(error);
      this.#setError(error instanceof Error ? error.message : 'Something went wrong');
      this.refs.addButton.disabled = false;
    } finally {
      this.#busy = false;
    }
  }

  #expand() {
    this.#expanded = true;
    this.refs.options.hidden = false;
    this.refs.addButton.setAttribute('aria-expanded', 'true');

    const confirm = this.refs.addButton.dataset.labelConfirm;
    if (confirm) this.refs.addButton.textContent = confirm;

    // Move focus to the selected pill so keyboard users land on the new control.
    const selected = this.refs.pills.querySelector('[data-plan-id].is-selected');
    if (selected instanceof HTMLElement) selected.focus();
  }

  #cartSectionIds() {
    const ids = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : undefined))
      .filter(Boolean);
    return Array.from(new Set(ids)).join(',');
  }

  /** @param {string} message */
  #setError(message) {
    const { error } = this.refs;
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }
}

if (!customElements.get('shavingart-cart-membership')) {
  customElements.define('shavingart-cart-membership', ShavingartCartMembership);
}
